<script setup lang="ts">
import { ref } from "vue";
import { Form } from "@primevue/forms";
import { defineAsyncComponent } from "vue";




const props = defineProps({
  appendClass: {
    type: Boolean,
    default: false,
  },
});

const formRef = ref();
const roles = ref([]);
if (props.appendClass) {
  roles.value = [
    {
      selectedRole: null,
      selectedLevel: null,
      quantity: 1,
      yearsOfExp: 1,
    },
  ];
} else {
  roles.value = [
    {
      selectedRole: null,
      selectedLevel: null,
      selectedContract: null,
      quantity: 1,
    },
  ];
}

const roleOptions = ref([
  { name: "Laravel Developer", value: "Laravel Developer" },
  { name: "Nuxt Developer", value: "Nuxt Developer" },
  { name: "Vue Developer", value: "Vue Developer" },
  { name: "Mobile App Developer", value: "Mobile App Developer" },
  { name: "Frontend Developer", value: "Frontend Developer" },
  { name: "Backend Developer", value: "Backend Developer" },
  { name: "UI Designer", value: "UI Designer" },
  { name: "Software Tester", value: "Software Tester" },
  { name: "DevOps Engineer", value: "DevOps Engineer" },
  { name: "System Administrator", value: "System Administrator" },
  { name: "Project Manager", value: "Project Manager" },
  { name: "Content Writer", value: "Content Writer" },
  { name: "Full-stack Developer", value: "Full-stack Developer" },
]);

const levelOptions = ref([
  { name: "Middle", value: "Middle" },
  { name: "Senior", value: "Senior" },
  { name: "Lead", value: "Lead" },
]);

const contractOptions = ref([
  { name: "1 Month", value: "1 Month" },
  { name: "3 Months", value: "3 Months" },
  { name: "6 Months", value: "6 Months" },
  { name: "1 Year", value: "1 Year" },
  { name: "2+ Years", value: "2+ Years" },
]);

// Capture initial state for reset
const initialState = JSON.parse(JSON.stringify(roles.value));

function addRoleSet() {
  if (props.appendClass) {
    roles.value.push({
      selectedRole: null,
      selectedLevel: null,
      quantity: 1,
      yearsOfExp: 1,
    });
  } else {
    roles.value.push({
      selectedRole: null,
      selectedLevel: null,
      selectedContract: null,
      quantity: 1,
    });
  }
}

function removeRoleSet(index: number) {
  if (roles.value.length > 1) {
    roles.value.splice(index, 1);
  }
}

function increment(field: "quantity" | "yearsOfExp", index: number) {
  roles.value[index][field]++;
}

function decrement(field: "quantity" | "yearsOfExp", index: number) {
  if (roles.value[index][field] > 1) {
    roles.value[index][field]--;
  }
}

function resetForm() {
  roles.value = JSON.parse(JSON.stringify(initialState));
}
const emit = defineEmits(["formSubmitted"]);

// Handle form submission
function submitForm() {
  emit("formSubmitted", roles.value);
  console.log(
    "Form submitted with roles:",
    JSON.parse(JSON.stringify(roles.value))
  );
}

const augmentationBenefits = ref([
  "Reasonable Cost",
  "Flexible Team",
  "No long term contract",
  "Add or remove on demand",
]);
</script>

<template>
  <div
    class="bg-[image:var(--border-form-card)] rounded-xl p-px relative shadow-[3px_23px_52.9px_-2px_#0000001A] w-full"
  >
    <div
      class="rounded-xl h-full py-4 md:py-6 px-4 md:px-10 bg-[image:var(--bg-form-card)]"
    >
      <h3 class="text-[22px] mb-7 text-center" v-if="!props.appendClass">
        Build your Product Team
      </h3>
      <div
        class="grid grid-cols-2 md:grid-cols-4 gap-0 justify-between max-w-screen-xl mx-auto mb-12"
        v-if="!props.appendClass"
      >
        <div
          v-for="benefit in augmentationBenefits"
          class="flex items-center md:gap-1"
        >
          <video autoplay loop muted playsinline width="34" height="34">
            <source src="/images/blue-tick.webm" type="video/webm" />
          </video>
          <p
            class="text-sm md:text-base text-[var(--color-9)] tracking-[0.5px]"
          >
            {{ benefit }}
          </p>
        </div>
      </div>

      <Form ref="formRef" class="contents">
        <div class="flex flex-col gap-6">
          <div
            v-for="(roleSet, index) in roles"
            :key="index"
            class="flex max-md:flex-col md:items-end border-[1px] rounded-xl dark:border-purple-700 p-4"
            :class="props.appendClass ? 'gap-4' : 'gap-6'"
          >
            <div
              class="md:max-w-[259px]"
              :class="props.appendClass ? 'min-w-[200px]' : 'w-full'"
            >
              <p class="mb-2.5">Project Role</p>
              <div
                class="p-px dark:bg-[image:var(--border-form-card)] bg-none bg-[#B5B5B5] rounded-lg"
              >
                <Select
                  v-model="roleSet.selectedRole"
                  :options="roleOptions"
                  optionLabel="name"
                  placeholder="select a role"
                  :pt="{
                    dropdownicon: {
                      class: 'dark:text-white h-full min-h-max ',
                    },
                  }"
                  class="w-full border-0 rounded-lg"
                />
              </div>
            </div>

            <div
              class="md:max-w-[259px]"
              :class="props.appendClass ? '' : 'w-full'"
            >
              <p class="mb-2.5">Level</p>
              <div
                class="p-px dark:bg-[image:var(--border-form-card)] bg-none bg-[#B5B5B5] rounded-lg"
              >
                <Select
                  v-model="roleSet.selectedLevel"
                  :options="levelOptions"
                  optionLabel="name"
                  placeholder="select a level"
                  :pt="{ dropdownicon: { class: 'dark:text-white' } }"
                  class="w-full border-0 rounded-lg"
                />
              </div>
            </div>

            <div class="md:max-w-[259px] w-full" v-if="!props.appendClass">
              <p class="mb-2.5">Contract</p>
              <div
                class="p-px dark:bg-[image:var(--border-form-card)] bg-none bg-[#B5B5B5] rounded-lg"
              >
                <Select
                  v-model="roleSet.selectedContract"
                  :options="contractOptions"
                  optionLabel="name"
                  placeholder="select a contract"
                  :pt="{ dropdownicon: { class: 'dark:text-white' } }"
                  class="w-full border-0 rounded-lg"
                />
              </div>
            </div>
            <div class="flex gap-4 items-end h-full max-sm:flex-wrap">
              <div
                class="max-sm:min-w-[80px] max-sm:max-w-[130px] md:min-w-[128px]"
                v-if="props.appendClass"
              >
                <p class="mb-2.5">Years Of Exp.</p>
                <div
                  class="p-px dark:bg-[image:var(--border-form-card)] bg-none bg-[#B5B5B5] rounded-lg relative"
                >
                  <InputText
                    v-model.number="roleSet.yearsOfExp"
                    type="number"
                    placeholder="1"
                    class="w-full border-0 rounded-lg !pr-16"
                  />
                  <div
                    class="flex gap-1 absolute top-1/2 -translate-y-1/2 right-2"
                  >
                    <button
                      @click="increment('yearsOfExp', index)"
                      aria-label="Increase value"
                      class="size-[26px] rounded-full px-2 border bg-[image:var(--bg-inc-button)] border-[#3F1974] grid place-content-center"
                    >
                      <WrSVGIcon name="WrPlus" class="size-3" />
                    </button>
                    <button
                      @click="decrement('yearsOfExp', index)"
                      aria-label="decrease value"
                      class="size-[26px] rounded-full px-2 border bg-[image:var(--bg-inc-button)] border-[#3F1974] grid place-content-center"
                    >
                      <WrSVGIcon name="WrMinus" class="size-3" />
                    </button>
                  </div>
                </div>
              </div>

              <div
                class="max-sm:min-w-[100px] max-sm:max-w-[130px] md:min-w-[128px]"
              >
                <p class="mb-2.5">Quantity</p>
                <div
                  class="p-px dark:bg-[image:var(--border-form-card)] bg-none bg-[#B5B5B5] rounded-lg relative"
                >
                  <InputText
                    v-model.number="roleSet.quantity"
                    type="number"
                    placeholder="1"
                    class="w-full border-0 rounded-lg py-3 !pr-16"
                  />
                  <div
                    class="flex gap-1 absolute top-1/2 -translate-y-1/2 right-2"
                  >
                    <button
                      @click="increment('quantity', index)"
                      aria-label="Increase value"
                      class="size-[26px] rounded-full border bg-[image:var(--bg-inc-button)] border-[#3F1974] grid place-content-center"
                    >

                      <WrSVGIcon name="WrPlus" class="size-3" />
                    </button>
                    <button
                      @click="decrement('quantity', index)"
                      aria-label="Decrease value"
                      class="size-[26px] rounded-full border bg-[image:var(--bg-inc-button)] border-[#3F1974] grid place-content-center"
                    >
                      <WrSVGIcon name="WrMinus" class="size-3" />
                    </button>
                  </div>
                </div>
              </div>
              <div class="flex gap-2 h-full items-center">
                <WrButton
                  container-class="min-w-max"
                  v-show="index === roles.length - 1"
                  @click.stop="addRoleSet"
                  type="button"
                >
                  Add role
                </WrButton>
                <button
                  v-show="roles.length > 1 && index !== roles.length - 1"
                  @click="removeRoleSet(index)"
                  class="text-white font-medium text-sm bg-red-500 rounded-lg p-2 px-2.5 flex gap-2 items-center justify-center"
                  type="button"
                >
                  Remove
                  <!-- <WrCrossIcon :fontSmall="true" /> -->
                  <WrSVGIcon name="WrCrossIcon" class="size-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="flex gap-2 mt-8">
          <WrButton @click="submitForm">Submit your request</WrButton>

          <div
            class="gradient-border-mask relative before:rounded-full before:bg-[linear-gradient(95.01deg,_#BDB1FF_2.72%,_#6952ED_95.35%)]"
          >
            <WrButton
              outlined
              append-class="z-10 relative"
              @click="resetForm"
              type="button"
            >
              Reset
            </WrButton>
          </div>
        </div>
      </Form>
    </div>
  </div>
</template>

<style>
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
}

input[type="number"] {
  -moz-appearance: textfield;
}
</style>
